import { useEffect, useMemo, useRef, useState } from "react";
import MiniSearch from "minisearch";
import { Search, X } from "lucide-react";
import { api } from "./api";
import type { SearchDocument } from "./types";

export function SearchDialog({ open, version, onClose, onOpen }: { open: boolean; version?: number; onClose: () => void; onOpen: (id: string) => void }) {
  const [documents, setDocuments] = useState<SearchDocument[]>([]);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    void api.search(version).then((result) => setDocuments(result.documents));
    setActiveIndex(0);
    queueMicrotask(() => inputRef.current?.focus());
  }, [open, version]);

  const index = useMemo(() => {
    const search = new MiniSearch<SearchDocument>({
      fields: ["title", "aliases", "text"],
      storeFields: ["id", "title", "type", "aliases"],
      searchOptions: { boost: { title: 4, aliases: 2 }, prefix: true, fuzzy: 0.18 }
    });
    search.addAll(documents);
    return search;
  }, [documents]);

  const results = query.trim() ? index.search(query.replaceAll("ё", "е"), { prefix: true, fuzzy: 0.18 }).slice(0, 20) : documents.slice(0, 12);
  const openResult = (result: { id: unknown }) => {
    onOpen(String(result.id));
    onClose();
  };
  if (!open) return null;

  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="search-dialog" role="dialog" aria-modal="true" aria-label="Поиск по Зазеркалью">
        <div className="search-field">
          <Search size={18} />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => { setQuery(event.target.value); setActiveIndex(0); }}
            onKeyDown={(event) => {
              if (event.key === "Escape") onClose();
              if (event.key === "ArrowDown") { event.preventDefault(); setActiveIndex((value) => Math.min(results.length - 1, value + 1)); }
              if (event.key === "ArrowUp") { event.preventDefault(); setActiveIndex((value) => Math.max(0, value - 1)); }
              if (event.key === "Enter" && results[activeIndex]) { event.preventDefault(); openResult(results[activeIndex]); }
            }}
            placeholder="Искать персонажа, место или объект…"
            aria-label="Поиск"
            aria-activedescendant={results[activeIndex] ? `search-result-${activeIndex}` : undefined}
          />
          <button className="icon-button compact" onClick={onClose} aria-label="Закрыть поиск"><X size={17} /></button>
        </div>
        <div className="search-results" role="listbox">
          {results.length ? results.map((result, indexValue) => (
            <button id={`search-result-${indexValue}`} key={String(result.id)} role="option" aria-selected={indexValue === activeIndex} className={`search-result ${indexValue === activeIndex ? "active" : ""}`} onMouseEnter={() => setActiveIndex(indexValue)} onClick={() => openResult(result)}>
              <span>{String(result.title)}</span>
              <small>{String(result.type || "note")} · {String(result.id)}</small>
            </button>
          )) : <p className="empty-state">Ничего не найдено. Попробуйте другое имя.</p>}
        </div>
        <footer className="dialog-footer"><kbd>↑↓</kbd> навигация <kbd>Enter</kbd> открыть <kbd>Esc</kbd> закрыть</footer>
      </section>
    </div>
  );
}
